import {
  ArrowLeft,
  Heart,
  Target,
  Shield,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

const AboutPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Heart,
      title: "Holistic Health",
      description: "Track nutrition, fitness, and wellness in one place",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      icon: Target,
      title: "Personalized Goals",
      description: "Custom recommendations based on your unique profile",
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      icon: Shield,
      title: "Privacy First",
      description: "Your health data is secure and private",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      icon: Sparkles,
      title: "Smart Insights",
      description: "AI-powered analysis to optimize your health",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
  ];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        backgroundColor: "white !important",
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-4 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            About Bite Balance
          </h1>
        </div>

        {/* App Logo/Banner */}
        <Card className="flex flex-col items-center rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg">
            <span className="text-3xl font-bold text-white">BB</span>
          </div>
          <h2 className="mb-1 text-2xl font-bold text-foreground">
            Bite Balance
          </h2>
          <p className="text-sm text-blue-600">
            Your Complete Health Companion
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Version 1.0.0</p>
        </Card>

        {/* Mission Statement */}
        <Card className="space-y-2 rounded-2xl border border-border bg-card p-4 shadow-sm">
          <h3 className="font-semibold text-foreground">Our Mission</h3>
          <p className="text-sm leading-relaxed text-blue-600">
            Bite Balance empowers you to take control of your health through
            intelligent tracking, personalized insights, and evidence-based
            recommendations. We believe that sustainable wellness comes from
            understanding your body and making informed choices every day.
          </p>
        </Card>

        {/* Key Features */}
        <div className="space-y-3">
          <h3 className="font-semibold text-foreground">What We Offer</h3>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className="flex items-start gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm"
              >
                <div
                  className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${feature.bgColor}`}
                >
                  <Icon className={`h-6 w-6 ${feature.color}`} />
                </div>
                <div className="flex-1">
                  <h4 className="mb-1 font-semibold text-foreground">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Legal Links */}
        <Card className="space-y-2 rounded-2xl border border-border bg-card p-3 shadow-sm">
          <button
            onClick={() =>
              navigate("/glucera" + GluceraNavigationRoutes.TERMS_OF_SERVICE)
            }
            className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-accent/50"
          >
            <span className="text-sm font-medium text-foreground">
              Terms of Service
            </span>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>
          <div className="h-px bg-border" />
          <button
            onClick={() =>
              navigate("/glucera" + GluceraNavigationRoutes.PRIVACY_POLICY)
            }
            className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-accent/50"
          >
            <span className="text-sm font-medium text-foreground">
              Privacy Policy
            </span>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>
          <div className="h-px bg-border" />
          <button
            onClick={() =>
              navigate("/glucera" + GluceraNavigationRoutes.LICENSES)
            }
            className="flex w-full items-center justify-between rounded-lg p-3 transition-colors hover:bg-accent/50"
          >
            <span className="text-sm font-medium text-foreground">
              Licenses
            </span>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>
        </Card>

        {/* Copyright */}
        <p className="pt-4 text-center text-xs text-muted-foreground">
          © 2026 Bite Balance. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AboutPage;
